<script setup>
import { computed, ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useToggle } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  assignedTeam: {
    type: Object,
    default: null,
  },
  assignedAgent: {
    type: Object,
    default: null,
  },
  teams: {
    type: Array,
    default: () => [],
  },
  teamMembersForTeam: {
    type: Function,
    required: true,
  },
  allAgents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();
const [isOpen, toggleOpen] = useToggle(false);
const step = ref(1);
const pendingTeam = ref(null);
const search = ref('');
const searchInput = ref(null);

const noneTeam = computed(() => ({
  id: 0,
  name: t('TEAMS_SETTINGS.LIST.NONE'),
}));

const noneAgent = computed(() => ({
  id: 0,
  name: t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE'),
}));

const hasTeam = computed(() => !!(props.assignedTeam && props.assignedTeam.id));
const hasAgent = computed(
  () => !!(props.assignedAgent && props.assignedAgent.id)
);
const hasAssignment = computed(() => hasTeam.value || hasAgent.value);

const teamLabel = computed(() =>
  hasTeam.value ? props.assignedTeam.name : t('TEAMS_SETTINGS.LIST.NONE')
);
const agentLabel = computed(() =>
  hasAgent.value
    ? props.assignedAgent.name
    : t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE')
);

const teamOptions = computed(() => {
  const options = [noneTeam.value, ...props.teams];
  if (!search.value) return options;
  return options.filter(team =>
    team.name.toLowerCase().includes(search.value.toLowerCase())
  );
});

const agentOptions = computed(() => {
  const baseAgents =
    pendingTeam.value && pendingTeam.value.id
      ? props.teamMembersForTeam(pendingTeam.value.id)
      : props.allAgents;
  const options = [noneAgent.value, ...baseAgents];
  if (!search.value) return options;
  return options.filter(agent =>
    agent.name.toLowerCase().includes(search.value.toLowerCase())
  );
});

const focusSearch = () => {
  nextTick(() => {
    searchInput.value?.focus();
  });
};

const open = () => {
  step.value = 1;
  pendingTeam.value = null;
  search.value = '';
  toggleOpen(true);
  focusSearch();
};

const close = () => {
  toggleOpen(false);
};

const onToggle = () => {
  if (isOpen.value) close();
  else open();
};

const onSelectTeam = team => {
  pendingTeam.value = team;
  step.value = 2;
  search.value = '';
  focusSearch();
};

const onSelectAgent = agent => {
  const team =
    pendingTeam.value && pendingTeam.value.id ? pendingTeam.value : null;
  const finalAgent = agent && agent.id ? agent : null;
  emit('select', { team, agent: finalAgent });
  close();
};

const goBack = () => {
  step.value = 1;
  search.value = '';
  focusSearch();
};

watch(isOpen, value => {
  if (!value) {
    step.value = 1;
    pendingTeam.value = null;
    search.value = '';
  }
});
</script>

<template>
  <OnClickOutside @trigger="close">
    <div class="relative w-full mb-2" @keyup.esc="close">
      <Button
        slate
        outline
        trailing-icon
        :icon="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="w-full !px-2"
        @click="onToggle"
      >
        <div class="flex items-center justify-between w-full min-w-0 gap-2">
          <h4
            v-if="!hasAssignment"
            class="m-0 text-sm leading-tight truncate text-n-slate-12"
          >
            {{ $t('CONVERSATION_SIDEBAR.ASSIGN_TEAM_AGENT_PLACEHOLDER') }}
          </h4>
          <h4
            v-else
            class="flex items-center gap-1 m-0 text-sm leading-tight truncate text-n-slate-12"
            :title="`${teamLabel} · ${agentLabel}`"
          >
            <span class="truncate">{{ teamLabel }}</span>
            <span
              class="text-n-slate-10 before:content-['·']"
              aria-hidden="true"
            />
            <span class="truncate">{{ agentLabel }}</span>
          </h4>
          <Avatar
            v-if="hasAgent"
            :name="assignedAgent.name"
            :src="assignedAgent.thumbnail"
            :status="assignedAgent.availability_status"
            :size="24"
            hide-offline-status
            rounded-full
          />
        </div>
      </Button>
      <div
        v-if="isOpen"
        class="box-border top-[2.625rem] w-full border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] absolute shadow-lg border-n-strong dark:border-n-strong p-2 z-[9999]"
      >
        <div class="flex items-center justify-between gap-1 mb-1">
          <Button
            v-if="step === 2"
            ghost
            slate
            xs
            icon="i-lucide-chevron-left"
            @click="goBack"
          />
          <h4
            class="flex-1 m-0 overflow-hidden text-sm text-n-slate-11 whitespace-nowrap text-ellipsis"
          >
            <template v-if="step === 1">
              {{ $t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM') }}
            </template>
            <template v-else>
              {{ $t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT') }}
              <span
                v-if="pendingTeam"
                class="ml-1 text-n-slate-10 before:content-['·_']"
                :title="
                  pendingTeam.id
                    ? pendingTeam.name
                    : $t('TEAMS_SETTINGS.LIST.NONE')
                "
              >
                {{
                  pendingTeam.id
                    ? pendingTeam.name
                    : $t('TEAMS_SETTINGS.LIST.NONE')
                }}
              </span>
            </template>
          </h4>
          <Button ghost slate xs icon="i-lucide-x" @click="close" />
        </div>
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="m-0 mb-2 w-full border border-solid border-transparent h-8 px-2 text-sm text-n-slate-12 rounded-md focus:border-n-brand bg-n-background dark:bg-n-background"
          :placeholder="
            step === 1
              ? $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
              : $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
          "
        />
        <div class="max-h-[10rem] overflow-auto">
          <template v-if="step === 1">
            <div
              v-for="team in teamOptions"
              :key="team.id"
              class="flex items-center min-h-7 p-1 rounded-md cursor-pointer text-n-slate-12 hover:bg-n-brand hover:text-white"
              @click="onSelectTeam(team)"
            >
              <fluent-icon
                :icon="team.id ? 'people-team' : 'dismiss-circle'"
                size="14"
                class="flex-shrink-0"
              />
              <p class="flex-1 mx-2 my-0 text-xs truncate">{{ team.name }}</p>
              <fluent-icon
                v-if="team.id"
                icon="chevron-right"
                size="12"
                class="flex-shrink-0"
              />
            </div>
            <h4
              v-if="!teamOptions.length"
              class="flex items-center justify-center w-full px-2 py-2 overflow-hidden text-sm text-n-slate-10 whitespace-nowrap text-ellipsis"
            >
              {{ $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM') }}
            </h4>
          </template>
          <template v-else>
            <div
              v-for="agent in agentOptions"
              :key="agent.id"
              class="flex items-center min-h-7 p-1 rounded-md cursor-pointer text-n-slate-12 hover:bg-n-brand hover:text-white"
              @click="onSelectAgent(agent)"
            >
              <fluent-icon
                v-if="!agent.id"
                icon="dismiss-circle"
                size="14"
                class="flex-shrink-0"
              />
              <Avatar
                v-else
                :name="agent.name"
                :src="agent.thumbnail"
                :status="agent.availability_status"
                :size="20"
                hide-offline-status
                rounded-full
                class="flex-shrink-0"
              />
              <p class="mx-2 my-0 text-xs truncate">{{ agent.name }}</p>
            </div>
            <h4
              v-if="!agentOptions.length"
              class="flex items-center justify-center w-full px-2 py-2 overflow-hidden text-sm text-n-slate-10 whitespace-nowrap text-ellipsis"
            >
              {{ $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT') }}
            </h4>
          </template>
        </div>
      </div>
    </div>
  </OnClickOutside>
</template>
